<!--
  Copyright (c) 2025 NoorWays. All rights reserved.
  Licensed under the terms specified in the LICENSE.txt file.
-->

<template>
  <!-- Dialog Backdrop -->
  <Teleport to="body">
    <Transition name="dialog-fade">
      <div
        v-if="isOpen"
        class="dialog-backdrop"
        @click="handleBackdropClick"
        role="presentation"
      >
        <!-- Dialog Container -->
        <div
          ref="dialogRef"
          class="dialog-container"
          role="dialog"
          :aria-labelledby="titleId"
          :aria-describedby="descriptionId"
          aria-modal="true"
          @click.stop
        >
          <!-- Dialog Header -->
          <div class="dialog-header">
            <h2 :id="titleId" class="dialog-title">
              {{ title }}
            </h2>
            <button
              class="dialog-close"
              @click="handleCancel"
              :aria-label="$t('common.close')"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <!-- Dialog Body -->
          <div :id="descriptionId" class="dialog-body">
            {{ message }}
          </div>

          <!-- Dialog Footer -->
          <div class="dialog-footer">
            <button
              ref="cancelButtonRef"
              class="dialog-btn dialog-btn-secondary"
              @click="handleCancel"
              :disabled="isProcessing"
            >
              {{ cancelText }}
            </button>
            <button
              class="dialog-btn dialog-btn-danger"
              @click="handleConfirm"
              :disabled="isProcessing"
              :aria-busy="isProcessing"
            >
              {{ isProcessing ? processingText : confirmText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onUnmounted } from 'vue'

interface Props {
  isOpen: boolean
  title: string
  message: string
  confirmText: string
  cancelText: string
  processingText?: string
  isProcessing?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  processingText: 'Processing...',
  isProcessing: false,
})

const emit = defineEmits<{
  confirm: []
  cancel: []
  'update:isOpen': [value: boolean]
}>()

const dialogRef = ref<HTMLElement | null>(null)
const cancelButtonRef = ref<HTMLButtonElement | null>(null)
const titleId = `dialog-title-${Math.random().toString(36).substring(2, 11)}`
const descriptionId = `dialog-desc-${Math.random().toString(36).substring(2, 11)}`

// Handle confirm action
function handleConfirm() {
  emit('confirm')
  emit('update:isOpen', false)
}

// Handle cancel action
function handleCancel() {
  emit('cancel')
  emit('update:isOpen', false)
}

// Handle backdrop click (close dialog)
function handleBackdropClick() {
  if (!props.isProcessing) {
    handleCancel()
  }
}

// Handle keyboard events
function handleKeydown(event: KeyboardEvent) {
  if (!props.isOpen) return

  // Close on Escape key
  if (event.key === 'Escape' && !props.isProcessing) {
    handleCancel()
  }

  // Trap focus within dialog
  if (event.key === 'Tab') {
    trapFocus(event)
  }
}

// Trap focus within dialog for accessibility
function trapFocus(event: KeyboardEvent) {
  if (!dialogRef.value) return

  const focusableElements = dialogRef.value.querySelectorAll<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  )

  const firstElement = focusableElements[0]
  const lastElement = focusableElements[focusableElements.length - 1]

  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault()
    lastElement?.focus()
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault()
    firstElement?.focus()
  }
}

// Watch for dialog open state changes
watch(
  () => props.isOpen,
  async (isOpen) => {
    if (isOpen) {
      // Focus the cancel button when dialog opens
      await nextTick()
      cancelButtonRef.value?.focus()

      // Prevent body scroll when dialog is open
      document.body.style.overflow = 'hidden'
    } else {
      // Restore body scroll when dialog closes
      document.body.style.overflow = ''
    }
  }
)

// Add/remove event listeners
onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  // Ensure body scroll is restored
  document.body.style.overflow = ''
})
</script>

<style lang="scss" scoped>
// Dialog backdrop overlay
.dialog-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--nw-bg-overlay, rgba(0, 0, 0, 0.5));
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--nw-z-modal, 1050);
  padding: var(--nw-space-md, 1rem);
}

// Dialog container
.dialog-container {
  background: var(--nw-bg-primary);
  border-radius: var(--nw-radius-xl, 1rem);
  box-shadow: var(--nw-shadow-xl);
  border: 1px solid var(--nw-border-light);
  max-width: 440px;
  width: 100%;
  max-height: 90vh;
  overflow: auto;
  animation: dialog-slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

// Dialog header
.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--nw-space-lg, 1.5rem);
  border-bottom: 1px solid var(--nw-border-light);
  gap: var(--nw-space-md, 1rem);
}

.dialog-title {
  margin: 0;
  font-size: var(--nw-font-size-xl, 1.25rem);
  font-weight: var(--nw-font-weight-semibold, 600);
  color: var(--nw-text-primary);
  line-height: var(--nw-line-height-tight, 1.25);
}

.dialog-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid var(--nw-border-light);
  border-radius: var(--nw-radius-md, 0.5rem);
  color: var(--nw-text-secondary);
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    color: var(--nw-text-primary);
    background: var(--nw-bg-tertiary);
    border-color: var(--nw-border-medium);
  }

  &:focus-visible {
    outline: 2px solid var(--nw-primary);
    outline-offset: 2px;
    box-shadow: var(--nw-focus-ring);
  }

  // Use a proper X icon instead of × character
  span {
    font-size: 1.25rem;
    line-height: 1;
    font-weight: 300;
  }
}

// Dialog body
.dialog-body {
  padding: var(--nw-space-lg, 1.5rem);
  color: var(--nw-text-secondary);
  font-size: var(--nw-font-size-base, 1rem);
  line-height: var(--nw-line-height-relaxed, 1.75);
}

// Dialog footer
.dialog-footer {
  display: flex;
  gap: var(--nw-space-sm, 0.5rem);
  padding: var(--nw-space-lg, 1.5rem);
  border-top: 1px solid var(--nw-border-light);
  justify-content: flex-end;
  background: var(--nw-bg-secondary);
  border-radius: 0 0 var(--nw-radius-xl, 1rem) var(--nw-radius-xl, 1rem);
}

// Dialog buttons
.dialog-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--nw-space-xs, 0.25rem);
  padding: var(--nw-space-sm, 0.5rem) var(--nw-space-lg, 1.5rem);
  border-radius: var(--nw-radius-lg, 0.75rem);
  font-weight: var(--nw-font-weight-semibold, 600);
  font-size: var(--nw-font-size-sm, 0.875rem);
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  white-space: nowrap;
  min-height: 44px;

  &:focus-visible {
    outline: 2px solid var(--nw-primary);
    outline-offset: 2px;
    box-shadow: var(--nw-focus-ring);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.dialog-btn-secondary {
  background: var(--nw-bg-primary);
  color: var(--nw-text-primary);
  border: 1px solid var(--nw-border-medium);

  &:hover:not(:disabled) {
    background: var(--nw-bg-tertiary);
    border-color: var(--nw-border-dark);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }
}

.dialog-btn-danger {
  background: linear-gradient(135deg, var(--nw-error) 0%, var(--nw-error-dark) 100%);
  color: var(--nw-white);
  box-shadow: 0 4px 14px rgba(239, 68, 68, 0.35);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, var(--nw-error-dark) 0%, #b91c1c 100%);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(239, 68, 68, 0.45);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.35);
  }
}

// Animations
.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition: opacity 0.25s ease;
}

.dialog-fade-enter-from,
.dialog-fade-leave-to {
  opacity: 0;
}

.dialog-fade-enter-active .dialog-container,
.dialog-fade-leave-active .dialog-container {
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.25s ease;
}

.dialog-fade-enter-from .dialog-container,
.dialog-fade-leave-to .dialog-container {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}

@keyframes dialog-slide-up {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

// Responsive adjustments
@media (max-width: 480px) {
  .dialog-backdrop {
    padding: var(--nw-space-sm, 0.5rem);
    align-items: flex-end;
  }

  .dialog-container {
    max-width: none;
    border-radius: var(--nw-radius-xl, 1rem) var(--nw-radius-xl, 1rem) 0 0;
    animation: dialog-slide-up-mobile 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .dialog-header,
  .dialog-body,
  .dialog-footer {
    padding: var(--nw-space-md, 1rem);
  }

  .dialog-footer {
    flex-direction: column-reverse;
    border-radius: 0;

    .dialog-btn {
      width: 100%;
    }
  }

  @keyframes dialog-slide-up-mobile {
    from {
      opacity: 0;
      transform: translateY(100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .dialog-fade-enter-from .dialog-container,
  .dialog-fade-leave-to .dialog-container {
    transform: translateY(100%);
  }
}
</style>

<!-- Dark theme overrides -->
<style lang="scss">
html.dark {
  .dialog-container {
    background: var(--nw-dark-bg-elevated, #1E293B);
    border-color: var(--nw-border-light);
  }

  .dialog-footer {
    background: var(--nw-dark-bg-base, #0F172A);
  }

  .dialog-btn-secondary {
    background: var(--nw-dark-bg-base, #0F172A);
    border-color: var(--nw-border-medium);

    &:hover:not(:disabled) {
      background: var(--nw-dark-bg-hover, #334155);
    }
  }
}
</style>

